/**
 * useLivePresence — envia pings periódicos ao backend para rastrear
 * a página atual do usuário no painel de Live Users do master.
 *
 * Gera um sessionId único por aba (sessionStorage) e envia via
 * POST /api/live-ping a cada 30 segundos e a cada mudança de rota.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const SESSION_KEY   = 'cc_live_sid';
const PING_INTERVAL = 30_000; // 30 segundos

function getOrCreateSessionId(): string {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    // crypto.randomUUID() é suportado em todos os browsers modernos
    sid = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

async function sendPing(page: string, title: string): Promise<void> {
  try {
    await fetch('/api/live-ping', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        sessionId: getOrCreateSessionId(),
        page,
        title,
      }),
    });
  } catch {
    // silencioso — não deve prejudicar a navegação
  }
}

export function useLivePresence(): void {
  const location     = useLocation();
  const intervalRef  = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const page  = location.pathname + location.search;
    const title = document.title;

    // Ping imediato ao entrar/trocar de página
    sendPing(page, title);

    // Ping periódico para manter presença ativa
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      sendPing(
        window.location.pathname + window.location.search,
        document.title,
      );
    }, PING_INTERVAL);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [location.pathname, location.search]);
}
